import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  apiListMembers,
  apiAddMember,
  apiUpdateMember,
  apiArchiveMember,
  apiRestoreMember,
  apiUpdateMemberLastSession,
} from '../api/membersApi';
import {
  apiListNotes,
  apiAddNote,
  apiUpdateNote,
} from '../api/notesApi';
import {
  apiListGoals,
  apiAddGoal,
  apiUpdateGoal,
} from '../api/goalsApi';
import {
  apiListMiniGoals,
  apiAddMiniGoal,
  apiUpdateMiniGoal,
} from '../api/miniGoalsApi';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [members, setMembers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [memberGoals, setMemberGoals] = useState([]);
  const [memberMiniGoals, setMemberMiniGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offlineMode, setOfflineMode] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Setup offline mode detection
  useEffect(() => {
    const handleOnlineStatus = () => {
      setOfflineMode(!navigator.onLine);
    };
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    setOfflineMode(!navigator.onLine);
    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  // Mock data for fallback
  const mockMembers = [
    {
      id: 1,
      name: 'Alex Thompson',
      email: 'alex@example.com',
      date_joined: '2023-01-15',
      country: 'USA',
      state: 'California',
      city: 'Los Angeles',
      phone: '555-1234',
      teams_id: '',
      programs: ['Amazon FBA/FBM'],
      last_session: new Date(
        Date.now() - 2 * 24 * 60 * 60 * 1000
      ).toISOString(),
      is_archived: false,
    },
    {
      id: 2,
      name: 'Maya Patel',
      email: 'maya@example.com',
      date_joined: '2023-02-20',
      country: 'Canada',
      state: 'Ontario',
      city: 'Toronto',
      phone: '555-2345',
      teams_id: '',
      programs: ['Social Media'],
      last_session: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      is_archived: false,
    },
  ];

  const mockNotes = [
    {
      id: 1,
      member_id: 1,
      coach_id: 1,
      coach_name: 'Sarah Johnson',
      title: 'Great progress in Amazon product selection',
      content:
        'Alex has shown significant improvement in his Amazon product research process.',
      category: 'Performance',
      timestamp: new Date(
        Date.now() - 2 * 24 * 60 * 60 * 1000
      ).toISOString(),
    },
  ];

  // Fetch data on initial load
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (navigator.onLine) {
          // 1. Members
          try {
            const apiMembers = await apiListMembers();
            if (Array.isArray(apiMembers)) {
              setMembers(
                apiMembers.sort((a, b) =>
                  (a.name || '').localeCompare(b.name || '')
                )
              );
            } else {
              setMembers([]);
            }
          } catch (err) {
            console.error('Error fetching members:', err);
            setMembers(mockMembers);
          }
          // 2. Notes
          try {
            const apiNotes = await apiListNotes();
            if (Array.isArray(apiNotes)) {
              setNotes(
                apiNotes.sort(
                  (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
                )
              );
            } else {
              setNotes([]);
            }
          } catch (err) {
            console.error('Error fetching notes:', err);
            setNotes(mockNotes);
          }
          // 3. Goals
          try {
            const apiGoals = await apiListGoals();
            if (Array.isArray(apiGoals)) {
              setMemberGoals(
                apiGoals.sort(
                  (a, b) => new Date(b.created_at) - new Date(a.created_at)
                )
              );
            } else {
              setMemberGoals([]);
            }
          } catch (err) {
            console.error('Error fetching goals:', err);
            setMemberGoals([]);
          }
          // 4. Mini Goals
          try {
            const apiMini = await apiListMiniGoals();
            if (Array.isArray(apiMini)) {
              setMemberMiniGoals(
                apiMini.sort(
                  (a, b) => new Date(b.created_at) - new Date(a.created_at)
                )
              );
            } else {
              setMemberMiniGoals([]);
            }
          } catch (err) {
            console.error('Error fetching mini goals:', err);
            setMemberMiniGoals([]);
          }
        } else {
          // Offline mode
          setMembers(mockMembers);
          setNotes(mockNotes);
          setMemberGoals([]);
          setMemberMiniGoals([]);
        }
      } catch (err) {
        console.error('Error in fetchData:', err);
        setMembers(mockMembers);
        setNotes(mockNotes);
        setMemberGoals([]);
        setMemberMiniGoals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Add a new note
  const addNote = async (noteData) => {
    try {
      const newNote = { ...noteData, timestamp: new Date().toISOString() };

      if (navigator.onLine) {
        try {
          const created = await apiAddNote(newNote);
          setNotes((prev) => [created, ...prev]);
          updateMemberLastSession(noteData.member_id);
          return created;
        } catch (error) {
          console.error('Error adding note:', error);
          // fallback local
          const localNote = { id: Date.now(), ...newNote };
          setNotes((prev) => [localNote, ...prev]);
          updateMemberLastSession(noteData.member_id);
          return localNote;
        }
      } else {
        const localNote = { id: Date.now(), ...newNote };
        setNotes((prev) => [localNote, ...prev]);
        updateMemberLastSession(noteData.member_id);
        return localNote;
      }
    } catch (err) {
      console.error('Error in addNote:', err);
      const localNote = {
        id: Date.now(),
        ...noteData,
        timestamp: new Date().toISOString(),
      };
      setNotes((prev) => [localNote, ...prev]);
      updateMemberLastSession(noteData.member_id);
      return localNote;
    }
  };

  // Update an existing note
  const updateNote = async (noteData) => {
    try {
      const { id, ...updateData } = noteData;
      if (navigator.onLine) {
        try {
          await apiUpdateNote(id, updateData);
        } catch (error) {
          console.error('Error updating note:', error);
        }
      }
      setNotes((prev) =>
        prev.map((note) =>
          note.id.toString() === id.toString()
            ? { ...note, ...updateData }
            : note
        )
      );
      return { id, ...updateData };
    } catch (err) {
      console.error('Error in updateNote:', err);
      return noteData;
    }
  };

  // Update member's last session date
  const updateMemberLastSession = async (memberId) => {
    try {
      const now = new Date().toISOString();
      if (navigator.onLine) {
        try {
          await apiUpdateMemberLastSession(memberId, now);
        } catch (error) {
          console.error('Error updating member last_session via NCB:', error);
        }
      }
      setMembers((prev) =>
        prev.map((member) =>
          member.id === memberId ? { ...member, last_session: now } : member
        )
      );
    } catch (err) {
      console.error('Error in updateMemberLastSession:', err);
    }
  };

  // Add a new member
  const addMember = async (memberData) => {
    try {
      const newMemberData = { ...memberData, is_archived: false };
      if (navigator.onLine) {
        try {
          const { id, ...dataToInsert } = newMemberData;
          const created = await apiAddMember(dataToInsert);
          setMembers((prev) => [...prev, created]);
          return created;
        } catch (error) {
          console.error('Error adding member via NCB:', error);
          const tempMember = { ...newMemberData, id: `temp_${Date.now()}` };
          setMembers((prev) => [...prev, tempMember]);
          return tempMember;
        }
      } else {
        const tempMember = { ...newMemberData, id: `temp_${Date.now()}` };
        setMembers((prev) => [...prev, tempMember]);
        return tempMember;
      }
    } catch (err) {
      console.error('Error in addMember:', err);
      throw err;
    }
  };

  // Update an existing member
  const updateMember = async (memberData) => {
    try {
      const { id, ...updateData } = memberData;
      if (navigator.onLine) {
        try {
          await apiUpdateMember(id, updateData);
        } catch (error) {
          console.error('Error updating member via NCB:', error);
          throw error;
        }
      }
      setMembers((prev) =>
        prev.map((member) =>
          member.id.toString() === id.toString()
            ? { ...member, ...updateData }
            : member
        )
      );
      return { id, ...updateData };
    } catch (err) {
      console.error('Error in updateMember:', err);
      throw err;
    }
  };

  // Archive a member
  const archiveMember = async (memberId) => {
    try {
      if (navigator.onLine) {
        try {
          await apiArchiveMember(memberId);
        } catch (error) {
          console.error('Error archiving member via NCB:', error);
          throw error;
        }
      }
      setMembers((prev) =>
        prev.map((member) =>
          member.id.toString() === memberId.toString()
            ? { ...member, is_archived: true }
            : member
        )
      );
      return true;
    } catch (err) {
      console.error('Error in archiveMember:', err);
      throw err;
    }
  };

  // Restore an archived member
  const restoreMember = async (memberId) => {
    try {
      if (navigator.onLine) {
        try {
          await apiRestoreMember(memberId);
        } catch (error) {
          console.error('Error restoring member via NCB:', error);
          throw error;
        }
      }
      setMembers((prev) =>
        prev.map((member) =>
          member.id.toString() === memberId.toString()
            ? { ...member, is_archived: false }
            : member
        )
      );
      return true;
    } catch (err) {
      console.error('Error in restoreMember:', err);
      throw err;
    }
  };

  // Member Goals Functions
  const getMemberGoals = (memberId) => {
    if (!memberId) return [];
    return memberGoals.filter(
      (goal) => String(goal.member_id) === String(memberId)
    );
  };

  const addMemberGoal = async (goalData) => {
    try {
      const newGoal = {
        ...goalData,
        created_at: new Date().toISOString(),
        is_archived: false,
      };

      if (navigator.onLine) {
        try {
          const created = await apiAddGoal(newGoal);
          setMemberGoals((prev) => [created, ...prev]);
          return created;
        } catch (error) {
          console.error('Error adding member goal:', error);
          const localGoal = { id: Date.now(), ...newGoal };
          setMemberGoals((prev) => [localGoal, ...prev]);
          return localGoal;
        }
      } else {
        const localGoal = { id: Date.now(), ...newGoal };
        setMemberGoals((prev) => [localGoal, ...prev]);
        return localGoal;
      }
    } catch (err) {
      console.error('Error in addMemberGoal:', err);
      const localGoal = {
        id: Date.now(),
        ...goalData,
        created_at: new Date().toISOString(),
      };
      setMemberGoals((prev) => [localGoal, ...prev]);
      return localGoal;
    }
  };

  const updateMemberGoal = async (goalData) => {
    try {
      const { id, ...updateData } = goalData;
      if (navigator.onLine) {
        try {
          await apiUpdateGoal(id, updateData);
        } catch (error) {
          console.error('Error updating member goal:', error);
        }
      }
      setMemberGoals((prev) =>
        prev.map((goal) =>
          goal.id.toString() === id.toString()
            ? { ...goal, ...updateData }
            : goal
        )
      );
      return { id, ...updateData };
    } catch (err) {
      console.error('Error in updateMemberGoal:', err);
      return goalData;
    }
  };

  const archiveMemberGoal = async (goalId) => {
    try {
      if (navigator.onLine) {
        await apiUpdateGoal(goalId, { is_archived: true });
      }
      setMemberGoals((prev) =>
        prev.map((goal) =>
          goal.id.toString() === goalId.toString()
            ? { ...goal, is_archived: true }
            : goal
        )
      );
      return true;
    } catch (err) {
      console.error('Error in archiveMemberGoal:', err);
      throw err;
    }
  };

  const restoreMemberGoal = async (goalId) => {
    try {
      if (navigator.onLine) {
        await apiUpdateGoal(goalId, { is_archived: false });
      }
      setMemberGoals((prev) =>
        prev.map((goal) =>
          goal.id.toString() === goalId.toString()
            ? { ...goal, is_archived: false }
            : goal
        )
      );
      return true;
    } catch (err) {
      console.error('Error in restoreMemberGoal:', err);
      throw err;
    }
  };

  // Member Mini-Goals Functions
  const getMemberMiniGoals = (memberId) => {
    if (!memberId) return [];
    return memberMiniGoals.filter(
      (goal) => String(goal.member_id) === String(memberId)
    );
  };

  const addMemberMiniGoal = async (goalData) => {
    try {
      const newGoal = {
        ...goalData,
        created_at: new Date().toISOString(),
        is_archived: false,
      };

      if (navigator.onLine) {
        try {
          const created = await apiAddMiniGoal(newGoal);
          setMemberMiniGoals((prev) => [created, ...prev]);
          return created;
        } catch (error) {
          console.error('Error adding member mini-goal:', error);
          const localGoal = { id: Date.now(), ...newGoal };
          setMemberMiniGoals((prev) => [localGoal, ...prev]);
          return localGoal;
        }
      } else {
        const localGoal = { id: Date.now(), ...newGoal };
        setMemberMiniGoals((prev) => [localGoal, ...prev]);
        return localGoal;
      }
    } catch (err) {
      console.error('Error in addMemberMiniGoal:', err);
      const localGoal = {
        id: Date.now(),
        ...goalData,
        created_at: new Date().toISOString(),
      };
      setMemberMiniGoals((prev) => [localGoal, ...prev]);
      return localGoal;
    }
  };

  const updateMemberMiniGoal = async (goalData) => {
    try {
      const { id, ...updateData } = goalData;
      if (navigator.onLine) {
        try {
          await apiUpdateMiniGoal(id, updateData);
        } catch (error) {
          console.error('Error updating member mini-goal:', error);
        }
      }
      setMemberMiniGoals((prev) =>
        prev.map((goal) =>
          goal.id.toString() === id.toString()
            ? { ...goal, ...updateData }
            : goal
        )
      );
      return { id, ...updateData };
    } catch (err) {
      console.error('Error in updateMemberMiniGoal:', err);
      return goalData;
    }
  };

  const archiveMemberMiniGoal = async (goalId) => {
    try {
      if (navigator.onLine) {
        await apiUpdateMiniGoal(goalId, { is_archived: true });
      }
      setMemberMiniGoals((prev) =>
        prev.map((goal) =>
          goal.id.toString() === goalId.toString()
            ? { ...goal, is_archived: true }
            : goal
        )
      );
      return true;
    } catch (err) {
      console.error('Error in archiveMemberMiniGoal:', err);
      throw err;
    }
  };

  const restoreMemberMiniGoal = async (goalId) => {
    try {
      if (navigator.onLine) {
        await apiUpdateMiniGoal(goalId, { is_archived: false });
      }
      setMemberMiniGoals((prev) =>
        prev.map((goal) =>
          goal.id.toString() === goalId.toString()
            ? { ...goal, is_archived: false }
            : goal
        )
      );
      return true;
    } catch (err) {
      console.error('Error in restoreMemberMiniGoal:', err);
      throw err;
    }
  };

  // Get notes for a specific member
  const getMemberNotes = (memberId) => {
    if (!memberId) return [];
    return notes.filter(
      (note) => String(note.member_id) === String(memberId)
    );
  };

  // Get recent notes
  const getRecentNotes = (limit = 10) => {
    return notes
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, limit);
  };

  // Search members by name or email
  const searchMembers = (query) => {
    if (!query) return [];
    query = query.toLowerCase();
    return members.filter(
      (member) =>
        member.name.toLowerCase().includes(query) ||
        member.email.toLowerCase().includes(query) ||
        (member.city && member.city.toLowerCase().includes(query))
    );
  };

  // Toggle archived members visibility
  const toggleShowArchived = () => {
    setShowArchived((prev) => !prev);
  };

  // Get active and/or archived members based on current filter
  const getFilteredMembers = () => {
    return showArchived
      ? members
      : members.filter((member) => !member.is_archived);
  };

  const value = {
    members: getFilteredMembers(),
    allMembers: members,
    notes,
    memberGoals,
    memberMiniGoals,
    loading,
    offlineMode,
    showArchived,
    toggleShowArchived,
    addNote,
    updateNote,
    addMember,
    updateMember,
    archiveMember,
    restoreMember,
    getMemberNotes,
    getRecentNotes,
    searchMembers,
    getMemberGoals,
    addMemberGoal,
    updateMemberGoal,
    archiveMemberGoal,
    restoreMemberGoal,
    getMemberMiniGoals,
    addMemberMiniGoal,
    updateMemberMiniGoal,
    archiveMemberMiniGoal,
    restoreMemberMiniGoal,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};